/**
 * The 30 SENDIG v3.1.1 datasets (domains), used to populate the closed-list
 * domain-override dropdown on the SEND Validator confirm step. This mirrors the
 * server's `allDomainCodes()` (source of truth for validation); the confirm
 * request is rejected server-side if a code is not in that set, so this list is
 * purely for presentation and must stay in sync with the bundled SENDIG spec.
 */
import { _isNil } from '@/littledash';

export interface SendDomain {
  code: string;
  name: string;
}

export const SENDIG_DOMAINS: ReadonlyArray<SendDomain> = [
  { code: 'TA', name: 'Trial Arms' },
  { code: 'TE', name: 'Trial Elements' },
  { code: 'TS', name: 'Trial Summary' },
  { code: 'TX', name: 'Trial Sets' },
  { code: 'DM', name: 'Demographics' },
  { code: 'CO', name: 'Comments' },
  { code: 'SE', name: 'Subject Elements' },
  { code: 'EX', name: 'Exposure' },
  { code: 'DS', name: 'Disposition' },
  { code: 'BW', name: 'Body Weights' },
  { code: 'BG', name: 'Body Weight Gains' },
  { code: 'CL', name: 'Clinical Observations' },
  { code: 'CV', name: 'Cardiovascular Test Results' },
  { code: 'DD', name: 'Death Diagnosis' },
  { code: 'EG', name: 'ECG Test Results' },
  { code: 'FW', name: 'Food and Water Consumption' },
  { code: 'LB', name: 'Laboratory Test Results' },
  { code: 'MA', name: 'Macroscopic Findings' },
  { code: 'MI', name: 'Microscopic Findings' },
  { code: 'OM', name: 'Organ Measurements' },
  { code: 'PC', name: 'Pharmacokinetics Concentrations' },
  { code: 'PP', name: 'Pharmacokinetics Parameters' },
  { code: 'PM', name: 'Palpable Masses' },
  { code: 'RE', name: 'Respiratory Test Results' },
  { code: 'SC', name: 'Subject Characteristics' },
  { code: 'TF', name: 'Tumor Findings' },
  { code: 'VS', name: 'Vital Signs' },
  { code: 'POOLDEF', name: 'Pool Definition' },
  { code: 'RELREC', name: 'Related Records' },
  { code: 'SUPPQUAL', name: 'Supplemental Qualifiers' },
];

/** react-select style options for the closed-list domain override dropdown. */
export const sendDomainOptions: Array<{ label: string; value: string }> = SENDIG_DOMAINS.map((domain) => ({
  label: `${domain.code} — ${domain.name}`,
  value: domain.code,
}));

/** Human label for a domain code, falling back to the bare code when unknown. */
export const sendDomainLabel = (code: string): string => {
  const match = SENDIG_DOMAINS.find((domain) => domain.code === code);
  return _isNil(match) ? code : `${match.code} — ${match.name}`;
};
